"use client";

import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { aggregateFeatures } from "@/lib/subscription/features";

// Bypass subscription checks when explicitly enabled in env.
const BYPASS_PAYMENTS = process.env.NEXT_PUBLIC_BYPASS_SUBSCRIPTION_GATES === "true";

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children, initialSubscription = null }) {
  const [subscription, setSubscription] = useState(initialSubscription);
  const [isLoading, setIsLoading] = useState(!initialSubscription);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState(null);

  // Fetch subscription status
  const fetchSubscription = useCallback(async () => {
    if (BYPASS_PAYMENTS) {
      setSubscription({ isActive: true, isTrial: false, tier: "CREATOR", hasSubscription: true });
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const response = await fetch("/api/subscription/status");
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch on mount if no initial subscription
  useEffect(() => {
    if (!initialSubscription) {
      fetchSubscription();
    }
  }, [initialSubscription, fetchSubscription]);

  const isActive = BYPASS_PAYMENTS ? true : (subscription?.isActive ?? false);
  const isTrial = BYPASS_PAYMENTS ? false : (subscription?.isTrial ?? false);
  const trialEndsAt = BYPASS_PAYMENTS ? null : (subscription?.trialEndsAt ?? subscription?.subscription?.trialEndsAt ?? null);
  const daysUntilTrialEnds = BYPASS_PAYMENTS ? null : (subscription?.daysUntilTrialEnds ?? null);
  const tier = BYPASS_PAYMENTS ? "CREATOR" : (subscription?.tier ?? null);
  const hasSubscription = BYPASS_PAYMENTS ? true : (subscription?.hasSubscription ?? false);
  const recordingSubscription = BYPASS_PAYMENTS ? null : (subscription?.recordingSubscription ?? null);
  const embedsSubscription = BYPASS_PAYMENTS ? null : (subscription?.embedsSubscription ?? null);

  // Aggregate the current user's capabilities across both product subs.
  // Components that need per-feature gating (paywall affordances, branding,
  // editor-export, etc.) read `features` directly instead of mapping tier→flag.
  const features = useMemo(() => {
    const subs = [recordingSubscription, embedsSubscription].filter(Boolean);
    return aggregateFeatures(subs);
  }, [recordingSubscription, embedsSubscription]);

  // Open paywall modal with optional feature context
  const openPaywall = useCallback((feature = null) => {
    setPaywallFeature(feature);
    setPaywallOpen(true);
  }, []);

  const closePaywall = useCallback(() => {
    setPaywallOpen(false);
    setPaywallFeature(null);
  }, []);

  // Check if subscription is required and open paywall if not active
  const requireSubscription = useCallback((feature = null) => {
    if (BYPASS_PAYMENTS) return true;
    if (!isActive) {
      openPaywall(feature);
      return false;
    }
    return true;
  }, [isActive, openPaywall]);

  // Check if a minimum tier is required
  const requireTier = useCallback((minTier, feature = null) => {
    if (BYPASS_PAYMENTS) return true;
    const tierHierarchy = { RECORDING: 1, EMBED: 2, CREATOR: 3 };

    if (!isActive) {
      openPaywall(feature);
      return false;
    }

    const currentLevel = tierHierarchy[tier] || 0;
    const requiredLevel = tierHierarchy[minTier] || 0;

    if (currentLevel < requiredLevel) {
      openPaywall(feature);
      return false;
    }

    return true;
  }, [isActive, tier, openPaywall]);

  const value = {
    subscription,
    isLoading,
    isActive,
    isTrial,
    trialEndsAt,
    daysUntilTrialEnds,
    tier,
    hasSubscription,
    recordingSubscription,
    embedsSubscription,
    features,
    paywallOpen,
    paywallFeature,
    openPaywall,
    closePaywall,
    requireSubscription,
    requireTier,
    refreshSubscription: fetchSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}

// Hook for quick subscription check without context
export function useRequireSubscription() {
  const { requireSubscription } = useSubscription();
  return requireSubscription;
}
